import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSocialPostsThunk, getSocialMediaCacheKey, SocialMediaPostType } from 'slices/influencer/thunk'
import { showSocialPostModal } from 'slices/ui/reducer'
import { AppDispatch, RootState } from 'store'
import { SocialPost } from 'types/campaign/collaborations'
import { Influencer, SocialAccount } from 'types/influencer'
import MediaSectionCarousel from './MediaSectionCarousel'

type MediaTab = 'reels' | 'posts' | 'videos'
type BrandCollabFilter = 'All' | 'Others' | string

const MEDIA_FETCH_CONFIGS_IG: Array<{
    type: SocialMediaPostType
    sort?: string
    has_brand_collabs?: 'y'
}> = [
    { type: 'REELS', sort: 'views_count' },
    { type: 'REELS', sort: 'timestamp_tz' },
    { type: 'REELS', has_brand_collabs: 'y' },
    { type: 'FEED', sort: 'views_count' },
    { type: 'FEED', sort: 'timestamp_tz' },
    { type: 'FEED', has_brand_collabs: 'y', sort: 'views_count' },
]

const MEDIA_FETCH_CONFIGS_YT: Array<{
    type: SocialMediaPostType
    sort?: string
    has_brand_collabs?: 'y'
}> = [
    { type: 'VIDEO', sort: 'views_count' },
    { type: 'VIDEO', sort: 'timestamp_tz' },
    { type: 'VIDEO', has_brand_collabs: 'y', sort: 'views_count' },
]

const getTabItems = (socialMedia: RootState['influencers']['social_media'][string], type: SocialMediaPostType) => {
    const top = socialMedia?.[getSocialMediaCacheKey(type, { sort: 'views_count' })]?.items ?? []
    const latest = socialMedia?.[getSocialMediaCacheKey(type, { sort: 'timestamp_tz' })]?.items ?? []
    const brand = socialMedia?.[getSocialMediaCacheKey(type, { has_brand_collabs: 'y' })]?.items ?? []
    return [...top, ...latest, ...brand]
}

const isTabFetched = (socialMedia: RootState['influencers']['social_media'][string], type: SocialMediaPostType) => {
    const keys = [
        getSocialMediaCacheKey(type, { sort: 'views_count' }),
        getSocialMediaCacheKey(type, { sort: 'timestamp_tz' }),
        getSocialMediaCacheKey(type, { has_brand_collabs: 'y' }),
    ]
    return keys.every((key) => Boolean(socialMedia?.[key]?.last_fetched_at))
}

interface ApplicationViewMediaProps {
    socialAccountId: string
    socialAccount: SocialAccount
    influencer?: Influencer
}

const ApplicationViewMedia: React.FC<ApplicationViewMediaProps> = ({ socialAccountId, socialAccount, influencer }) => {
    const dispatch = useDispatch<AppDispatch>()
    const [activeTab, setActiveTab] = useState<MediaTab>(socialAccount.platform === 'yt' ? 'videos' : 'reels')
    const [brandFilter, setBrandFilter] = useState<BrandCollabFilter>('All')

    const mediaType: SocialMediaPostType =
        socialAccount.platform === 'yt'
            ? 'VIDEO'
            : activeTab === 'reels'
              ? 'REELS'
              : 'FEED'
    const socialMedia = useSelector((state: RootState) => state.influencers.social_media[socialAccountId] ?? {})
    const loading = useSelector((state: RootState) => state.influencers.mediaLoading)

    useEffect(() => {
        if (!socialAccountId) return

        const fetchConfigs = socialAccount.platform === 'yt' ? MEDIA_FETCH_CONFIGS_YT : MEDIA_FETCH_CONFIGS_IG

        fetchConfigs.forEach(({ type, sort, has_brand_collabs }) => {
            dispatch(fetchSocialPostsThunk({
                page: 1,
                socialAccountId,
                type,
                sort,
                limit: 10,
                has_brand_collabs,
            }))
        })
    }, [dispatch, socialAccount.platform, socialAccountId])

    const reelsReady = socialAccount.platform !== 'yt' ? isTabFetched(socialMedia, 'REELS') : true
    const postsReady = socialAccount.platform !== 'yt' ? isTabFetched(socialMedia, 'FEED') : true
    const videosReady = socialAccount.platform === 'yt' ? isTabFetched(socialMedia, 'VIDEO') : true

    const hasReelsData = socialAccount.platform !== 'yt' ? getTabItems(socialMedia, 'REELS').length > 0 : false
    const hasPostsData = socialAccount.platform !== 'yt' ? getTabItems(socialMedia, 'FEED').length > 0 : false
    const hasVideosData = socialAccount.platform === 'yt' ? getTabItems(socialMedia, 'VIDEO').length > 0 : false

    // Keep tabs visible; show an empty state when there's no content.
    const showReelsTab = socialAccount.platform !== 'yt'
    const showPostsTab = socialAccount.platform !== 'yt'
    const showVideosTab = socialAccount.platform === 'yt'

    const showTabs = true

    useEffect(() => {
        if (socialAccount.platform === 'yt') {
            setActiveTab('videos')
            return
        }
        if (!showReelsTab && activeTab === 'reels' && showPostsTab) {
            setActiveTab('posts')
        }
        if (!showPostsTab && activeTab === 'posts' && showReelsTab) {
            setActiveTab('reels')
        }
    }, [activeTab, showPostsTab, showReelsTab, socialAccount.platform])

    const handleMediaClick = (post: SocialPost) => {
        dispatch(showSocialPostModal({ postId: post.uuid, influencer, socialAccount }))
    }

    const topPerformingItems = useMemo(() => {
        const cacheKey = getSocialMediaCacheKey(mediaType, { sort: 'views_count' })
        return socialMedia[cacheKey]?.items ?? []
    }, [mediaType, socialMedia])

    const latestItems = useMemo(() => {
        const cacheKey = getSocialMediaCacheKey(mediaType, { sort: 'timestamp_tz' })
        return socialMedia[cacheKey]?.items ?? []
    }, [mediaType, socialMedia])

    const brandCollabItems = useMemo(() => {
        const cacheKey = getSocialMediaCacheKey(mediaType, { has_brand_collabs: 'y' })
        return socialMedia[cacheKey]?.items ?? []
    }, [mediaType, socialMedia])

    useEffect(() => {
        setBrandFilter('All')
    }, [activeTab])

    const brandCollabFilters = useMemo(() => {
        const nicheSet = new Set<string>()
        let hasOthers = false

        for (const item of brandCollabItems) {
            const niche = item.speciality_niche_tier1
            if (typeof niche === 'string' && niche.trim().length > 0) {
                nicheSet.add(niche.trim())
            } else {
                hasOthers = true
            }
        }

        const niches = Array.from(nicheSet).sort((a, b) => a.localeCompare(b))
        const filters: BrandCollabFilter[] = ['All', ...niches]
        // Show "Others" only when there is at least one other niche tab (besides All)
        if (hasOthers && niches.length > 0) filters.push('Others')
        return filters
    }, [brandCollabItems])

    const filteredBrandCollabItems = useMemo(() => {
        if (brandFilter === 'All') return brandCollabItems
        if (brandFilter === 'Others') {
            return brandCollabItems.filter((item) => {
                const niche = item.speciality_niche_tier1
                return !(typeof niche === 'string' && niche.trim().length > 0)
            })
        }
        return brandCollabItems.filter((item) => (item.speciality_niche_tier1 ?? '').trim() === brandFilter)
    }, [brandCollabItems, brandFilter])

    const topTitle =
        socialAccount.platform === 'yt'
            ? 'Top Performing Videos'
            : activeTab === 'reels'
              ? 'Top Performing Reels'
              : 'Top Performing Posts'

    const latestTitle =
        socialAccount.platform === 'yt'
            ? 'Latest Videos'
            : activeTab === 'reels'
              ? 'Latest Reels'
              : 'Latest Posts'
    const hasActiveTabContent = topPerformingItems.length > 0 || latestItems.length > 0 || brandCollabItems.length > 0

    const activeTabReady =
        socialAccount.platform === 'yt'
            ? videosReady
            : activeTab === 'reels'
              ? reelsReady
              : postsReady

    const showEmptyState = !loading && activeTabReady && !hasActiveTabContent

    return (
        <div className="application-view-media">
            <h2 className="application-view-media__heading">Creator Media</h2>

            {showTabs && (
                <div className="application-view-media__tabs">
                    {showReelsTab && (
                        <button
                            type="button"
                            className={`application-view-media__tab ${activeTab === 'reels' ? 'application-view-media__tab--active' : ''}`}
                            onClick={() => setActiveTab('reels')}
                        >
                            Reel
                        </button>
                    )}
                    {showPostsTab && (
                        <button
                            type="button"
                            className={`application-view-media__tab ${activeTab === 'posts' ? 'application-view-media__tab--active' : ''}`}
                            onClick={() => setActiveTab('posts')}
                        >
                            Posts
                        </button>
                    )}
                    {showVideosTab && (
                        <button
                            type="button"
                            className={`application-view-media__tab ${activeTab === 'videos' ? 'application-view-media__tab--active' : ''}`}
                            onClick={() => setActiveTab('videos')}
                        >
                            Videos
                        </button>
                    )}
                </div>
            )}

            <div className="application-view-media__content">
                <MediaSectionCarousel
                    title={topTitle}
                    items={topPerformingItems}
                    loading={loading}
                    onItemClick={handleMediaClick}
                />
                <MediaSectionCarousel
                    title={latestTitle}
                    items={latestItems}
                    loading={loading}
                    onItemClick={handleMediaClick}
                />
                <MediaSectionCarousel
                    title="Brand Collabs"
                    items={filteredBrandCollabItems}
                    loading={loading}
                    showCollaborator={true}
                    headerSlot={
                        brandCollabFilters.length > 1 ? (
                            <div className="application-view-media__filters">
                                {brandCollabFilters.map((filter) => (
                                    <button
                                        key={filter}
                                        type="button"
                                        className={`filters-badge ${brandFilter === filter ? 'active' : ''}`}
                                        onClick={() => setBrandFilter(filter)}
                                    >
                                        {filter}
                                    </button>
                                ))}
                            </div>
                        ) : null
                    }
                    onItemClick={handleMediaClick}
                />
                {showEmptyState ? (
                    <div className="application-view-media__empty">
                        <div className="application-view-media__empty-inner">
                            <i className="bx bx-image-alt application-view-media__empty-icon" aria-hidden />
                            <div className="application-view-media__empty-text">No media available.</div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default ApplicationViewMedia
